// src/components/bouquet/MoneyBouquet3D.tsx
import React, {
  useRef,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import { cn } from "../../utils/helpers";

interface MoneyBouquet3DProps {
  amount: number;
  petals: number;
  isInteractive?: boolean;
  isFrozen?: boolean;
  className?: string;
  onPetalClick?: () => void;
  primaryColor?: string;
  secondaryColor?: string;
  collectedCount?: number;
}

export const MoneyBouquet3D: React.FC<MoneyBouquet3DProps> = ({
  amount,
  petals,
  isInteractive = true,
  isFrozen = false,
  className,
  onPetalClick,
  primaryColor = "#E11D48",
  secondaryColor = "#000000",
}) => {
  const controls = useAnimation();
  const [collectedIds, setCollectedIds] = useState<Set<string>>(new Set());
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Generate organic 3D petal positions
  const petals3D = useMemo(() => {
    const items = [];
    const numItems = Math.min(petals, 40);

    for (let i = 0; i < numItems; i++) {
      // Organic spiral distribution
      const goldenAngle = i * 137.5; // Golden angle for natural distribution
      const radius = 80 + Math.sin(i * 2) * 30;
      const x = Math.cos((goldenAngle * Math.PI) / 180) * radius;
      const y = Math.sin((goldenAngle * Math.PI) / 180) * radius * 0.7;
      const z = Math.cos(i) * 40 + Math.sin(i) * 30;

      items.push({
        id: `petal-${i}`,
        position: { x, y, z },
        rotation: {
          x: Math.random() * 30 - 15,
          y: goldenAngle,
          z: Math.random() * 60 - 30,
        },
        scale: 0.7 + Math.random() * 0.5,
        delay: i * 0.05,
        flutterPhase: Math.random() * Math.PI * 2,
      });
    }
    return items;
  }, [petals]);

  // Gentle rotation when interactive
  useEffect(() => {
    if (isInteractive && !isFrozen) {
      controls.start({
        rotateY: 360,
        transition: { duration: 40, repeat: Infinity, ease: "linear" },
      });
    } else {
      controls.stop();
    }
  }, [isInteractive, isFrozen, controls]);

  const handlePetalClick = useCallback(
    (petalId: string) => {
      if (!isInteractive) return;

      setCollectedIds((prev) => {
        const next = new Set(prev);
        next.add(petalId);
        return next;
      });

      onPetalClick?.();
    },
    [isInteractive, onPetalClick],
  );

  return (
    <motion.div
      ref={containerRef}
      animate={controls}
      className={cn(
        "relative w-full h-full flex items-center justify-center",
        className,
      )}
      style={{
        transformStyle: "preserve-3d",
        perspective: "1500px",
      }}
    >
      <AnimatePresence>
        {petals3D.map((petal) => {
          if (collectedIds.has(petal.id)) return null;

          const isHovered = hoveredId === petal.id;

          return (
            <motion.div
              key={petal.id}
              className="absolute cursor-pointer z-50 touch-none"
              initial={{ x: 0, y: 0, z: 0, scale: 0, opacity: 0 }}
              animate={{
                x: petal.position.x,
                y: petal.position.y,
                z: petal.position.z,
                rotateX: petal.rotation.x,
                rotateY: petal.rotation.y,
                rotateZ: petal.rotation.z,
                scale: petal.scale,
                opacity: 1,
                transition: {
                  delay: petal.delay,
                  duration: 1.5,
                  ease: [0.22, 1, 0.36, 1],
                },
              }}
              exit={{
                scale: 0,
                x: 0,
                y: 0,
                opacity: 0,
                transition: { duration: 0.6, ease: "backIn" },
              }}
              whileHover={{
                scale: petal.scale * 1.15,
                z: petal.position.z + 30,
                transition: { duration: 0.3 },
              }}
              onHoverStart={() => setHoveredId(petal.id)}
              onHoverEnd={() => setHoveredId(null)}
              onClick={(e) => {
                e.stopPropagation();
                handlePetalClick(petal.id);
              }}
              style={{ transformStyle: "preserve-3d" }}
            >
              {/* Organic petal design */}
              <div className="relative w-14 h-20 md:w-16 md:h-24 pointer-events-auto">
                {/* Main petal shape */}
                <div
                  className="absolute inset-0 rounded-[60%_40%_50%_50%] shadow-xl backdrop-blur-sm border border-white/20"
                  style={{
                    background: `linear-gradient(135deg, ${primaryColor}dd 0%, ${primaryColor}66 100%)`,
                    boxShadow: isHovered
                      ? `0 0 30px ${primaryColor}cc`
                      : `0 10px 20px rgba(0,0,0,0.3)`,
                  }}
                />

                {/* Value indicator (subtle) */}
                <div className="absolute inset-0 flex items-center justify-center -rotate-12">
                  <span className="text-[8px] font-bold text-white/60 tracking-tighter">
                    KES
                  </span>
                </div>

                {/* Shine line */}
                <div className="absolute top-1/4 left-1/4 w-px h-1/2 bg-white/30 rounded-full transform -rotate-45" />

                {/* Edge highlight */}
                <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-white/10 to-transparent rounded-b-full" />
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>

      {/* Breathing core - emotional center */}
      <div className="absolute" style={{ transform: "translateZ(20px)" }}>
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="w-40 h-40 rounded-full blur-3xl"
          style={{ backgroundColor: primaryColor }}
        />
      </div>

      {/* Center glow */}
      <div className="absolute w-20 h-20 rounded-full bg-white/10 blur-xl" />
    </motion.div>
  );
};
